export type ShopMoney = {
  amount: string;
  currencyCode: string;
};

export type ShopImage = {
  url: string;
  altText: string | null;
};

export type ShopVariant = {
  id: string;
  title: string;
  availableForSale: boolean;
  price: ShopMoney;
  image: ShopImage | null;
};

export type ShopProduct = {
  id: string;
  handle: string;
  title: string;
  description: string;
  descriptionHtml: string;
  vendor: string;
  productType: string;
  availableForSale: boolean;
  featuredImage: ShopImage | null;
  images: ShopImage[];
  priceRange: {
    minVariantPrice: ShopMoney;
    maxVariantPrice: ShopMoney;
  };
  variants: ShopVariant[];
};

export type ShopCollectionPageInfo = {
  hasNextPage: boolean;
  endCursor: string | null;
};

export type ShopCollection = {
  id: string;
  handle: string;
  title: string;
  description: string;
  image: ShopImage | null;
  products: ShopProduct[];
  productsPageInfo: ShopCollectionPageInfo;
};

export type ShopCartLineInput = {
  merchandiseId: string;
  quantity: number;
};

export type ShopCheckoutSession = {
  checkoutUrl: string;
  totalQuantity: number;
  subtotal: ShopMoney;
  total: ShopMoney;
};

const rawStoreDomain = import.meta.env.VITE_SHOPIFY_STORE_DOMAIN as string | undefined;
const storefrontToken = import.meta.env.VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN as string | undefined;
const storefrontApiVersion =
  (import.meta.env.VITE_SHOPIFY_STOREFRONT_API_VERSION as string | undefined) ?? '2025-10';

function normalizeStoreDomain(domain: string) {
  return domain.replace(/^https?:\/\//, '').replace(/\/$/, '');
}

const storeDomain = rawStoreDomain ? normalizeStoreDomain(rawStoreDomain) : '';

export const isShopifyConfigured = !!storeDomain && !!storefrontToken;

export const shopifyConfigError = isShopifyConfigured
  ? null
  : 'Shop is not configured yet. Configure VITE_SHOPIFY_STORE_DOMAIN and VITE_SHOPIFY_STOREFRONT_ACCESS_TOKEN in the customer app build environment, then rebuild or redeploy.';

type ShopifyGraphQLResponse<T> = {
  data?: T;
  errors?: Array<{ message: string }>;
};

type ShopifyProductNode = {
  id: string;
  handle: string;
  title: string;
  description: string;
  descriptionHtml: string;
  vendor: string;
  productType: string;
  availableForSale: boolean;
  featuredImage: ShopImage | null;
  images: { nodes: ShopImage[] };
  priceRange: {
    minVariantPrice: ShopMoney;
    maxVariantPrice: ShopMoney;
  };
  variants: { nodes: ShopVariant[] };
};

type ShopifyPageInfo = {
  hasNextPage: boolean;
  endCursor: string | null;
};

type ShopifyCollectionNode = {
  id: string;
  handle: string;
  title: string;
  description: string;
  image: ShopImage | null;
  products?: { nodes: ShopifyProductNode[]; pageInfo?: ShopifyPageInfo };
};

const PRODUCTS_QUERY = `
  query Products($first: Int!) {
    products(first: $first, sortKey: BEST_SELLING) {
      nodes {
        id
        handle
        title
        description
        descriptionHtml
        vendor
        productType
        availableForSale
        featuredImage {
          url
          altText
        }
        images(first: 8) {
          nodes {
            url
            altText
          }
        }
        priceRange {
          minVariantPrice {
            amount
            currencyCode
          }
          maxVariantPrice {
            amount
            currencyCode
          }
        }
        variants(first: 25) {
          nodes {
            id
            title
            availableForSale
            price {
              amount
              currencyCode
            }
            image {
              url
              altText
            }
          }
        }
      }
    }
  }
`;

const PRODUCT_QUERY = `
  query Product($handle: String!) {
    product(handle: $handle) {
      id
      handle
      title
      description
      descriptionHtml
      vendor
      productType
      availableForSale
      featuredImage {
        url
        altText
      }
      images(first: 12) {
        nodes {
          url
          altText
        }
      }
      priceRange {
        minVariantPrice {
          amount
          currencyCode
        }
        maxVariantPrice {
          amount
          currencyCode
        }
      }
      variants(first: 50) {
        nodes {
          id
          title
          availableForSale
          price {
            amount
            currencyCode
          }
          image {
            url
            altText
          }
        }
      }
    }
  }
`;

const COLLECTIONS_QUERY = `
  query Collections($first: Int!) {
    collections(first: $first) {
      nodes {
        id
        handle
        title
        description
        image {
          url
          altText
        }
      }
    }
  }
`;

const COLLECTION_QUERY = `
  query Collection($handle: String!, $first: Int!, $after: String) {
    collection(handle: $handle) {
      id
      handle
      title
      description
      image {
        url
        altText
      }
      products(first: $first, after: $after, sortKey: BEST_SELLING) {
        pageInfo {
          hasNextPage
          endCursor
        }
        nodes {
          id
          handle
          title
          description
          descriptionHtml
          vendor
          productType
          availableForSale
          featuredImage {
            url
            altText
          }
          images(first: 8) {
            nodes {
              url
              altText
            }
          }
          priceRange {
            minVariantPrice {
              amount
              currencyCode
            }
            maxVariantPrice {
              amount
              currencyCode
            }
          }
          variants(first: 25) {
            nodes {
              id
              title
              availableForSale
              price {
                amount
                currencyCode
              }
              image {
                url
                altText
              }
            }
          }
        }
      }
    }
  }
`;

const CREATE_CART_MUTATION = `
  mutation CreateCart($input: CartInput!) {
    cartCreate(input: $input) {
      cart {
        checkoutUrl
        totalQuantity
        cost {
          subtotalAmount {
            amount
            currencyCode
          }
          totalAmount {
            amount
            currencyCode
          }
        }
      }
      userErrors {
        field
        message
      }
    }
  }
`;

async function shopifyFetch<T>(query: string, variables?: Record<string, unknown>) {
  if (!isShopifyConfigured) {
    throw new Error(shopifyConfigError ?? 'Shopify is not configured.');
  }

  const response = await fetch(`https://${storeDomain}/api/${storefrontApiVersion}/graphql.json`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-Shopify-Storefront-Access-Token': storefrontToken!,
    },
    body: JSON.stringify({ query, variables }),
  });

  const payload = (await response.json()) as ShopifyGraphQLResponse<T>;

  if (!response.ok) {
    throw new Error(payload.errors?.[0]?.message ?? 'Shop request failed.');
  }

  if (payload.errors?.length) {
    throw new Error(payload.errors[0].message);
  }

  if (!payload.data) {
    throw new Error('Shopify returned no data.');
  }

  return payload.data;
}

function mapProduct(node: ShopifyProductNode): ShopProduct {
  return {
    id: node.id,
    handle: node.handle,
    title: node.title,
    description: node.description,
    descriptionHtml: node.descriptionHtml,
    vendor: node.vendor,
    productType: node.productType,
    availableForSale: node.availableForSale,
    featuredImage: node.featuredImage,
    images: node.images.nodes,
    priceRange: node.priceRange,
    variants: node.variants.nodes,
  };
}

function mapCollection(node: ShopifyCollectionNode): ShopCollection {
  return {
    id: node.id,
    handle: node.handle,
    title: node.title,
    description: node.description,
    image: node.image,
    products: node.products?.nodes.map(mapProduct) ?? [],
    productsPageInfo: {
      hasNextPage: node.products?.pageInfo?.hasNextPage ?? false,
      endCursor: node.products?.pageInfo?.endCursor ?? null,
    },
  };
}

export function formatShopifyMoney(money: ShopMoney | null | undefined) {
  if (!money) return '';
  return new Intl.NumberFormat('en-AE', {
    style: 'currency',
    currency: money.currencyCode,
    maximumFractionDigits: 2,
  }).format(Number(money.amount));
}

export function getShopProductCategory(product: Pick<ShopProduct, 'productType' | 'vendor'>) {
  const category = product.productType.trim() || product.vendor.trim();
  return category || 'Shop picks';
}

export function getShopCollectionLabels(products: ShopProduct[]) {
  const unique = new Set<string>();

  products.forEach((product) => {
    unique.add(getShopProductCategory(product));
  });

  return [...unique];
}

export async function getShopCollections(first = 8) {
  const data = await shopifyFetch<{ collections: { nodes: ShopifyCollectionNode[] } }>(COLLECTIONS_QUERY, { first });
  return data.collections.nodes.map(mapCollection);
}

export async function getShopCollection(
  handle: string,
  options?: { first?: number; after?: string | null },
) {
  const first = options?.first ?? 24;
  const after = options?.after ?? null;
  const data = await shopifyFetch<{ collection: ShopifyCollectionNode | null }>(
    COLLECTION_QUERY,
    { handle, first, after },
  );
  return data.collection ? mapCollection(data.collection) : null;
}

export async function getShopProducts(first = 24) {
  const data = await shopifyFetch<{ products: { nodes: ShopifyProductNode[] } }>(PRODUCTS_QUERY, { first });
  return data.products.nodes.map(mapProduct);
}

export async function getShopProduct(handle: string) {
  const data = await shopifyFetch<{ product: ShopifyProductNode | null }>(PRODUCT_QUERY, { handle });
  return data.product ? mapProduct(data.product) : null;
}

export async function createShopCheckout(lines: ShopCartLineInput[], email?: string) {
  const data = await shopifyFetch<{
    cartCreate: {
      cart: {
        checkoutUrl: string;
        totalQuantity: number;
        cost: {
          subtotalAmount: ShopMoney;
          totalAmount: ShopMoney;
        };
      } | null;
      userErrors: Array<{ message: string }>;
    };
  }>(CREATE_CART_MUTATION, {
    input: {
      lines,
      buyerIdentity: email ? { email } : undefined,
    },
  });

  if (data.cartCreate.userErrors.length > 0) {
    throw new Error(data.cartCreate.userErrors[0].message);
  }

  if (!data.cartCreate.cart) {
    throw new Error('Shopify checkout could not be created.');
  }

  return {
    checkoutUrl: data.cartCreate.cart.checkoutUrl,
    totalQuantity: data.cartCreate.cart.totalQuantity,
    subtotal: data.cartCreate.cart.cost.subtotalAmount,
    total: data.cartCreate.cart.cost.totalAmount,
  } satisfies ShopCheckoutSession;
}